import { defineComponent as _defineComponent } from 'vue'
import { ExpensesMode, User } from "@/CodegenTypes";
import { mutations, queries } from "@/apollo";
import { getRoleCreatePermission } from "@/helpers/getRoleCreatePermission";
import { clone } from "lodash";
import { errorMutationNotification } from "@/utils";
import { ZERO_TO_HUNDRED_DECIMAL_PERCENT, ZERO_TO_HUNDRED_PERCENT, ZERO_TO_THIRTY_PERCENT } from "@/constants";
import { computed, onMounted, ref, watch } from "vue";
import { apolloClient } from "@/apollo";
import { addSuccessToast } from "@/vue-service";
import { useTranslation } from "@/store/translation";
import { FEATURE_TOGGLES } from "@/config";


export default /*#__PURE__*/_defineComponent({
  __name: 'TTGameSettingForm',
  props: {
    upline: null,
    user: null
  },
  emits: ["close"],
  setup(__props: any, { emit }) {

const props = __props as {
  upline: User;
  user: User;
};




const { $t } = useTranslation();

const form = ref(getDefaultForm());
const loading = ref(false);

const canIntake = computed(() => {
  const isMember = props.user.roleName === "member";
  const uplineEnabledIntake = props.upline.ttDownlineIntake;
  return !isMember && uplineEnabledIntake;
});

const showPlaceOut = computed(() => {
  return (
    props.user.roleName !== "member" &&
    (props.upline.ttD1PlaceoutPl > 0 ||
      props.upline.ttD2PlaceoutPl > 0 ||
      props.upline.ttD3PlaceoutPl > 0 ||
      props.upline.ttD4PlaceoutPl > 0)
  );
});

onMounted(() => {
  resetForm();
});

const allowedRoles = computed(() => {
  if (!props.user) {
    return [];
  }
  return getRoleCreatePermission(props.user.roleName);
});

const currUserId = computed(() => {
  return props.user.userId;
});

const showUplinePl = computed(() => {
  return props.user.intakeType === "layer";
});

const showManagementExpense = computed(() => {
  return !FEATURE_TOGGLES.useV1Legacy && props.user.roleName === "manager";
});

function toggleModal(val: boolean) {
  if (!val) {
    emit("close", val);
  }
}

function getDefaultForm() {
  return clone({
    ttEnabled: props.user.ttEnabled,
    ttManagementExpensesMode: props.user.ttManagementExpensesMode,
    ttManagementExpenses: props.user.ttManagementExpenses,
    ttManagementExpensesManagerTicketInPercent: props.user.ttManagementExpensesManagerTicketInPercent,
    ttShareCompanyExpenses: props.user.ttShareCompanyExpenses,
    ttDownlineIntake: props.user.ttDownlineIntake,
    ttD1TicketRebate: props.user.ttD1TicketRebate,
    ttD2TicketRebate: props.user.ttD2TicketRebate,
    ttD3TicketRebate: props.user.ttD3TicketRebate,
    ttD4TicketRebate: props.user.ttD4TicketRebate,
    ttD1Payout: props.user.ttD1Payout,
    ttD2Payout: props.user.ttD2Payout,
    ttD3Payout: props.user.ttD3Payout,
    ttD4Payout: props.user.ttD4Payout,
    ttD1CompanyPl: props.user.ttD1CompanyPl,
    ttD2CompanyPl: props.user.ttD2CompanyPl,
    ttD3CompanyPl: props.user.ttD3CompanyPl,
    ttD4CompanyPl: props.user.ttD4CompanyPl,
    ttD1PlaceoutPl: props.user.ttD1PlaceoutPl,
    ttD2PlaceoutPl: props.user.ttD2PlaceoutPl,
    ttD3PlaceoutPl: props.user.ttD3PlaceoutPl,
    ttD4PlaceoutPl: props.user.ttD4PlaceoutPl,
    ttD1UplinePl: props.user.ttD1UplinePl,
    ttD2UplinePl: props.user.ttD2UplinePl,
    ttD3UplinePl: props.user.ttD3UplinePl,
    ttD4UplinePl: props.user.ttD4UplinePl,
    ttIntake: props.user.ttIntake,
    ttIntakeD1: props.user.ttIntakeD1,
    ttIntakeD2: props.user.ttIntakeD2,
    ttIntakeD3: props.user.ttIntakeD3,
    ttIntakeD4: props.user.ttIntakeD4,
    ttIntakeTax: props.user.ttIntakeTax,
  });
}

const expensesPercentOptions = computed(() => {
  const options = ZERO_TO_HUNDRED_DECIMAL_PERCENT;
  return options;
});

watch(
  () => props.user,
  () => {
    resetForm();
  }
);

function resetForm() {
  form.value = getDefaultForm();
}

const ticketRebateOptions = computed(() => {
  return ZERO_TO_THIRTY_PERCENT;
});

const companyPlD1Options = computed(() => {
  const options = ZERO_TO_HUNDRED_PERCENT;
  if (props.user.intakeType !== "shadow") {
    return options.filter((opt) => opt.value <= props.upline.ttD1CompanyPl);
  }
  return options;
});

const companyPlD2Options = computed(() => {
  const options = ZERO_TO_HUNDRED_PERCENT;
  if (props.user.intakeType !== "shadow") {
    return options.filter((opt) => opt.value <= props.upline.ttD2CompanyPl);
  }
  return options;
});

const companyPlD3Options = computed(() => {
  const options = ZERO_TO_HUNDRED_PERCENT;
  if (props.user.intakeType !== "shadow") {
    return options.filter((opt) => opt.value <= props.upline.ttD3CompanyPl);
  }
  return options;
});

const companyPlD4Options = computed(() => {
  const options = ZERO_TO_HUNDRED_PERCENT;
  if (props.user.intakeType !== "shadow") {
    return options.filter((opt) => opt.value <= props.upline.ttD4CompanyPl);
  }
  return options;
});

const placeoutPlD1Options = computed(() => {
  if (FEATURE_TOGGLES.useV1Legacy) {
    return ZERO_TO_HUNDRED_PERCENT.filter(
      (opt) => opt.value <= props.upline.ttD1PlaceoutPl
    );
  }
  return ZERO_TO_HUNDRED_PERCENT;
});
const placeoutPlD2Options = computed(() => {
  if (FEATURE_TOGGLES.useV1Legacy) {
    return ZERO_TO_HUNDRED_PERCENT.filter(
      (opt) => opt.value <= props.upline.ttD2PlaceoutPl
    );
  }
  return ZERO_TO_HUNDRED_PERCENT;
});
const placeoutPlD3Options = computed(() => {
  if (FEATURE_TOGGLES.useV1Legacy) {
    return ZERO_TO_HUNDRED_PERCENT.filter(
      (opt) => opt.value <= props.upline.ttD3PlaceoutPl
    );
  }
  return ZERO_TO_HUNDRED_PERCENT;
});
const placeoutPlD4Options = computed(() => {
  if (FEATURE_TOGGLES.useV1Legacy) {
    return ZERO_TO_HUNDRED_PERCENT.filter(
      (opt) => opt.value <= props.upline.ttD4PlaceoutPl
    );
  }
  return ZERO_TO_HUNDRED_PERCENT;
});

const uplinePlD1Options = computed(() => {
  return ZERO_TO_HUNDRED_PERCENT.filter(
    (opt) => opt.value <= props.upline.ttD1UplinePl
  );
});

const uplinePlD2Options = computed(() => {
  return ZERO_TO_HUNDRED_PERCENT.filter(
    (opt) => opt.value <= props.upline.ttD2UplinePl
  );
});

const uplinePlD3Options = computed(() => {
  return ZERO_TO_HUNDRED_PERCENT.filter(
    (opt) => opt.value <= props.upline.ttD3UplinePl
  );
});

const uplinePlD4Options = computed(() => {
  return ZERO_TO_HUNDRED_PERCENT.filter(
    (opt) => opt.value <= props.upline.ttD4UplinePl
  );
});

async function handleSubmit() {
  loading.value = true;
  try {
    const legacyExpensePayload = FEATURE_TOGGLES.useV1Legacy
      ? {
          ttShareCompanyExpenses: form.value.ttShareCompanyExpenses,
        }
      : {
          ttManagementExpensesMode: form.value.ttManagementExpensesMode,
          ttManagementExpenses:
            form.value.ttManagementExpensesMode === ExpensesMode.Fixed
              ? form.value.ttManagementExpenses
              : undefined,
          ttManagementExpensesManagerTicketInPercent:
            form.value.ttManagementExpensesMode ===
            ExpensesMode.ManagerTicketInPercent
              ? form.value.ttManagementExpensesManagerTicketInPercent
              : undefined,
        };

    const result = await apolloClient.mutate({
      mutation: mutations.updateTtGameSetting,
      variables: {
        payload: {
          behalfOf: currUserId.value,
          ttEnabled: form.value.ttEnabled,
          ...legacyExpensePayload,
          ttDownlineIntake: form.value.ttDownlineIntake,
          ttD1TicketRebate: form.value.ttD1TicketRebate,
          ttD2TicketRebate: form.value.ttD2TicketRebate,
          ttD3TicketRebate: form.value.ttD3TicketRebate,
          ttD4TicketRebate: form.value.ttD4TicketRebate,
          ttD1Payout: form.value.ttD1Payout,
          ttD2Payout: form.value.ttD2Payout,
          ttD3Payout: form.value.ttD3Payout,
          ttD4Payout: form.value.ttD4Payout,
          ttD1CompanyPl: form.value.ttD1CompanyPl,
          ttD2CompanyPl: form.value.ttD2CompanyPl,
          ttD3CompanyPl: form.value.ttD3CompanyPl,
          ttD4CompanyPl: form.value.ttD4CompanyPl,
          ttD1PlaceoutPl: form.value.ttD1PlaceoutPl,
          ttD2PlaceoutPl: form.value.ttD2PlaceoutPl,
          ttD3PlaceoutPl: form.value.ttD3PlaceoutPl,
          ttD4PlaceoutPl: form.value.ttD4PlaceoutPl,
          ttD1UplinePl: form.value.ttD1UplinePl,
          ttD2UplinePl: form.value.ttD2UplinePl,
          ttD3UplinePl: form.value.ttD3UplinePl,
          ttD4UplinePl: form.value.ttD4UplinePl,
          ttIntake: form.value.ttIntake,
          ttIntakeD1: form.value.ttIntakeD1,
          ttIntakeD2: form.value.ttIntakeD2,
          ttIntakeD3: form.value.ttIntakeD3,
          ttIntakeD4: form.value.ttIntakeD4,
          ttIntakeTax: form.value.ttIntakeTax,
        },
      },
      refetchQueries: [
        {
          query: queries.user,
          variables: {
            userId: props.user.uplineId,
          },
        },
        {
          query: queries.user,
          variables: {
            userId: props.user.userId,
          },
        },
      ],
    });
    loading.value = false;
    addSuccessToast("TOTO Game setting updated.");
    // toggleModal(false);
  } catch (err) {
    loading.value = false;
    errorMutationNotification(err);
  }
}

return { __sfc: true,props, emit, $t, form, loading, canIntake, showPlaceOut, allowedRoles, currUserId, showUplinePl, showManagementExpense, toggleModal, getDefaultForm, expensesPercentOptions, resetForm, ticketRebateOptions, companyPlD1Options, companyPlD2Options, companyPlD3Options, companyPlD4Options, placeoutPlD1Options, placeoutPlD2Options, placeoutPlD3Options, placeoutPlD4Options, uplinePlD1Options, uplinePlD2Options, uplinePlD3Options, uplinePlD4Options, handleSubmit, ExpensesMode }
}

})